import React, { useRef, useState, useEffect } from "react";
import Calendar from "./Calendar";
import ClockTimePicker from "./TimePicker";

interface CustomDateTimePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
  readOnly?: boolean; // New prop to control editability
  error?: string;
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  readOnly = false, // Default is editable
  error,
}) => {
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("12:30");

  const applyTimeToDate = (date: Date | null, time: string): Date | null => {
    if (!date) return null;
    const [hours, minutes] = time.split(":").map(Number);
    const updated = new Date(date);
    updated.setHours(hours, minutes, 0, 0);
    return updated;
  };

  // Update time strings when dates change externally
  useEffect(() => {
    if (startDate) {
      const hours = startDate.getHours().toString().padStart(2, "0");
      const minutes = startDate.getMinutes().toString().padStart(2, "0");
      setStartTime(`${hours}:${minutes}`);
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      const hours = endDate.getHours().toString().padStart(2, "0");
      const minutes = endDate.getMinutes().toString().padStart(2, "0");
      setEndTime(`${hours}:${minutes}`);
    }
  }, [endDate]);

  const handleDateChange = (newStart: Date | null, newEnd: Date | null) => {
    if (readOnly) return; // Prevent changes if readOnly is true
    let updatedStart = newStart ? applyTimeToDate(newStart, startTime) : null;
    let updatedEnd;
    if (newEnd) {
      updatedEnd = applyTimeToDate(newEnd, endTime);

      if (updatedEnd && updatedStart && updatedEnd < updatedStart) {
        const temp = updatedStart;
        updatedStart = updatedEnd;
        updatedEnd = temp;
      }
    } else {
      updatedEnd = null;
    }

    onDateChange(updatedStart, updatedEnd);
  };

  const handleTimeSelect = (time: string) => {
    if (readOnly) return; // Prevent changes if readOnly is true
    if (focusedField === "start") {
      setStartTime(time);
      const updatedStart = applyTimeToDate(startDate, time);
      onDateChange(updatedStart, endDate);
    } else if (focusedField === "end") {
      setEndTime(time);
      const updatedEnd = applyTimeToDate(endDate, time);
      onDateChange(startDate, updatedEnd);
    }
    setFocusedField(null);
  };

  const [focusedField, setFocusedField] = useState<"start" | "end" | null>(
    null,
  );
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        focusedField &&
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node)
      ) {
        setFocusedField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [focusedField]);

  return (
    <div className="flex flex-col gap-4" ref={pickerRef}>
      <div>
        <label
          className={`mb-3 block text-[11px] font-bold uppercase tracking-[0.22em] ${
            error ? "text-red-500" : "text-slate-700"
          }`}
        >
          Booking Dates{error ? " *" : ""}
        </label>
        <div
          className={`rounded-[28px] bg-white p-5 ${
            error ? "border border-red-200" : "ring-1 ring-[#e5e7eb]"
          }`}
        >
          <Calendar
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className="relative grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1 relative">
          <label className="mb-3 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700">
            Start Time
          </label>
          <button
            className={`h-14 w-full rounded-[20px] border bg-white px-5 text-left text-base text-slate-700 outline-none transition ${
              readOnly
                ? "cursor-not-allowed border-[#e5e7eb] bg-[#f3f4f6] text-slate-500"
                : "border-[#e5e7eb] hover:bg-[#f3f4f6]"
            }`}
            onClick={() => !readOnly && setFocusedField("start")}
            type="button"
            disabled={readOnly}
          >
            {startTime}
          </button>
          {focusedField === "start" && !readOnly && (
            <div className="absolute left-0 top-full z-20 mt-2 flex max-w-full sm:right-auto">
              <ClockTimePicker value={startTime} onChange={handleTimeSelect} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 relative">
          <label className="mb-3 block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700">
            End Time
          </label>
          <button
            type="button"
            className={`h-14 w-full rounded-[20px] border bg-white px-5 text-left text-base text-slate-700 outline-none transition ${
              readOnly
                ? "cursor-not-allowed border-[#e5e7eb] bg-[#f3f4f6] text-slate-500"
                : "border-[#e5e7eb] hover:bg-[#f3f4f6]"
            }`}
            onClick={() => !readOnly && setFocusedField("end")}
            disabled={readOnly}
          >
            {endTime}
          </button>
          {focusedField === "end" && !readOnly && (
            <div className="absolute left-0 top-full z-20 mt-2 flex max-w-full sm:right-0 sm:left-auto sm:justify-end">
              <ClockTimePicker value={endTime} onChange={handleTimeSelect} />
            </div>
          )}
        </div>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default CustomDateTimePicker;
